import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import styled from 'styled-components';
import { parseFrac } from '../utils/parseFrac';

/* ── Convierte valor fraccionario + unidad a metros ── */
function toM(val, unit) {
  const n = parseFrac(val);
  if (unit === 'in') return (n * 2.54) / 100;
  return n / 100;
}

/* ── Tabla genérica (Box) ── */
function Board({ position, size, color = '#C8973A' }) {
  return (
    <mesh position={position} castShadow receiveShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial color={color} roughness={0.8} metalness={0.05} />
    </mesh>
  );
}

/* ── Distribuye N tablas con anchos individuales dentro de un span total ──
   widths: array de metros por tabla (todos positivos)
   span: espacio total disponible (metros)
   Retorna array de { pos (centro), size } con espaciado uniforme entre tablas */
function distributeBoards(widths, span) {
  const n = widths.length;
  if (n === 0) return [];
  if (n === 1) return [{ pos: 0, size: widths[0] }];
  const totalW = widths.reduce((a, b) => a + b, 0);
  const gap = Math.max(0, (span - totalW) / (n - 1));
  const result = [];
  let cursor = -span / 2;
  for (let i = 0; i < n; i++) {
    const w = widths[i];
    result.push({ pos: cursor + w / 2, size: w });
    cursor += w + gap;
  }
  return result;
}

/* ── Distribuye N tablas usando separaciones explícitas (gap borde a borde) ──
   seps: array de {sep, unit} de longitud N-1
   defaultSep: separación por defecto en metros (puede ser 0) */
function distributeWithSeps(widths, seps, defaultSep) {
  const n = widths.length;
  if (n === 0) return [];
  if (n === 1) return [{ pos: 0, size: widths[0] }];
  const gaps = Array.from({ length: n - 1 }, (_, i) => {
    const s = seps[i];
    if (s && s.sep !== '') return toM(s.sep, s.unit || 'in') || defaultSep;
    return defaultSep;
  });
  const totalW = widths.reduce((a, b) => a + b, 0);
  const totalGap = gaps.reduce((a, b) => a + b, 0);
  const totalSpan = totalW + totalGap;
  const result = [];
  let cursor = -totalSpan / 2;
  for (let i = 0; i < n; i++) {
    const w = widths[i];
    result.push({ pos: cursor + w / 2, size: w });
    cursor += w + (i < n - 1 ? gaps[i] : 0);
  }
  return result;
}

/* ── Modelo Polín / Rifle ──────────────────────────────────────────
   Superior e inferior: tablas planas (corren en Z, distribuidas en X).
   Largueros: corren en X, con 2 cortes redondeados en la parte inferior
   (para entrada de montacargas). Geometría del larguero (vista lateral):

     [████████████████████████████████████████████████]  ← parte superior
     [pata izq] [╰──corte1──╯] [pata centro] [╰──corte2──╯] [pata der]

   Los 3 pies (izq, centro, der) son iguales en ancho: FOOT_W = (LL-2×NOTCH_W)/3
   Los cortes tienen las esquinas superiores redondeadas.                    */
function PalletPolin({ dims, units, tablasDet, seps }) {
  const model = useMemo(() => {
    const det = tablasDet || { superior: [], larguero: [], inferior: [] };

    // Largueros: corren en X (pallet LARGO), distribuidos en Z (pallet ANCHO)
    const LL = toM(dims.larguero?.largo,    units.larguero?.largo)    || 1.25;
    const LA = toM(dims.larguero?.ancho,    units.larguero?.ancho)    || 0.076;
    const LH = toM(dims.larguero?.alto,     units.larguero?.alto)     || 0.09;
    const LQ = parseInt(dims.larguero?.qty) || 4;

    // Superiores: corren en Z (pallet ANCHO), distribuidas en X
    const SL = toM(dims.superior?.largo,    units.superior?.largo)    || 1.18;
    const SA = toM(dims.superior?.ancho,    units.superior?.ancho)    || 0.076;
    const SH = toM(dims.superior?.alto,     units.superior?.alto)     || 0.016;
    const SQ = parseInt(dims.superior?.qty) || 7;

    // Inferiores: mismo sentido que superiores (Z), distribuidas en X
    const IL = toM(dims.inferior?.largo,    units.inferior?.largo)    || 1.18;
    const IA = toM(dims.inferior?.ancho,    units.inferior?.ancho)    || 0.076;
    const IH = toM(dims.inferior?.alto,     units.inferior?.alto)     || 0.016;
    const IQ = parseInt(dims.inferior?.qty) || 3;

    // Cortes del larguero (resaque = alto del corte, corte = ancho de cada corte)
    const NOTCH_H = toM(dims.larguero?.resaque, units.larguero?.resaque) || Math.min(LH * 0.38, 0.034);
    const NOTCH_W = toM(dims.larguero?.corte,   units.larguero?.corte)   || Math.min(LL * 0.27, 0.20);
    // Ancho de cada pie (3 iguales: izq, centro, der)
    const FOOT_W  = Math.max((LL - 2 * NOTCH_W) / 3, 0.02);

    const palletLargo = LL;
    const palletAncho = SL;

    // Y local (dentro del grupo centrado verticalmente)
    const yBase  = IH;               // inferior de los largueros (encima de tablas inf.)
    const supCY  = IH + LH + SH / 2; // centro Y de las tablas superiores
    const totalH = IH + LH + SH;

    // Posiciones X de los límites del perfil del larguero
    const x0 = -LL / 2;
    const x1 = x0 + FOOT_W;     // fin pata izq  /  inicio corte 1
    const x2 = x1 + NOTCH_W;    // fin corte 1   /  inicio pata centro
    const x3 = x2 + FOOT_W;     // fin pata ctr  /  inicio corte 2
    const x4 = x3 + NOTCH_W;    // fin corte 2   /  inicio pata der
    const x5 = LL / 2;

    // Radio de las esquinas redondeadas en la parte superior de cada corte
    const R = Math.min(NOTCH_H * 0.5, NOTCH_W * 0.3, 0.025);

    // Perfil 2D del larguero en el plano X-Y (Y=0 base, Y=LH parte superior).
    // Se recorre en sentido horario comenzando desde la esquina inferior izquierda.
    const shape = new THREE.Shape();
    shape.moveTo(x0, 0);
    shape.lineTo(x0, LH);               // sube por el lado izquierdo
    shape.lineTo(x5, LH);               // techo completo
    shape.lineTo(x5, 0);                // baja por el lado derecho (pata der)
    // ── Corte 2 (derecho) ─────────────────────────────────────────
    shape.lineTo(x4, 0);                // base de pata der → inicio corte 2
    shape.lineTo(x4, NOTCH_H - R);      // sube pared derecha del corte 2
    shape.quadraticCurveTo(x4, NOTCH_H, x4 - R, NOTCH_H); // esquina sup-der redondeada
    shape.lineTo(x3 + R, NOTCH_H);     // techo del corte 2
    shape.quadraticCurveTo(x3, NOTCH_H, x3, NOTCH_H - R); // esquina sup-izq redondeada
    shape.lineTo(x3, 0);               // baja pared izquierda del corte 2 (= pata ctr derecha)
    // ── Corte 1 (izquierdo) ───────────────────────────────────────
    shape.lineTo(x2, 0);               // base de pata ctr → inicio corte 1
    shape.lineTo(x2, NOTCH_H - R);     // sube pared derecha del corte 1
    shape.quadraticCurveTo(x2, NOTCH_H, x2 - R, NOTCH_H); // esquina sup-der redondeada
    shape.lineTo(x1 + R, NOTCH_H);    // techo del corte 1
    shape.quadraticCurveTo(x1, NOTCH_H, x1, NOTCH_H - R); // esquina sup-izq redondeada
    shape.lineTo(x1, 0);              // baja pared izquierda del corte 1 (= pata izq derecha)
    shape.lineTo(x0, 0);             // base de pata izq → cierra el perfil

    const extrudeSettings = { steps: 1, depth: LA, bevelEnabled: false };

    // ── Distribuciones ────────────────────────────────────────────
    const infWidths = Array.from({ length: IQ }, (_, i) => {
      const d = det.inferior[i];
      return (d && d.ancho !== '') ? (toM(d.ancho, d.unit || units.inferior?.ancho) || IA) : IA;
    });
    const infDist = distributeBoards(infWidths, palletLargo);

    const largWidths = Array.from({ length: LQ }, (_, i) => {
      const d = det.larguero[i];
      return (d && d.ancho !== '') ? (toM(d.ancho, d.unit || units.larguero?.ancho) || LA) : LA;
    });
    const sepArr = seps?.larguero || [];
    const defaultSepM = toM(dims.larguero?.separacion, units.larguero?.separacion);
    const hasSeps = defaultSepM > 0 || sepArr.some(s => s && s.sep !== '');
    const largDist = hasSeps
      ? distributeWithSeps(largWidths, sepArr, defaultSepM)
      : distributeBoards(largWidths, palletAncho);

    const supWidths = Array.from({ length: SQ }, (_, i) => {
      const d = det.superior[i];
      return (d && d.ancho !== '') ? (toM(d.ancho, d.unit || units.superior?.ancho) || SA) : SA;
    });
    const supDist = distributeBoards(supWidths, palletLargo);

    return {
      infBoards:      infDist.map(b => ({ posX: b.pos, sX: b.size, IH, sZ: IL })),
      largDist,
      shape,
      extrudeSettings,
      LA, LH, yBase,
      supBoards:      supDist.map(b => ({ posX: b.pos, sX: b.size, SH, sZ: SL })),
      supCY, totalH,
    };
  }, [dims, units, tablasDet]);

  return (
    <group position={[0, -model.totalH / 2, 0]}>
      {/* Tablas inferiores — planas al fondo */}
      {model.infBoards.map((b, i) => (
        <Board key={`pi-${i}`} position={[b.posX, b.IH / 2, 0]} size={[b.sX, b.IH, b.sZ]} color="#B8864A" />
      ))}
      {/* Largueros: perfil extruido con 3 pies y 2 cortes redondeados */}
      {model.largDist.map((larg, i) => (
        <mesh
          key={`pl-${i}`}
          position={[0, model.yBase, larg.pos - model.LA / 2]}
          castShadow receiveShadow
        >
          <extrudeGeometry args={[model.shape, model.extrudeSettings]} />
          <meshStandardMaterial color="#C8973A" roughness={0.8} metalness={0.05} />
        </mesh>
      ))}
      {/* Tablas superiores */}
      {model.supBoards.map((b, i) => (
        <Board key={`ps-${i}`} position={[b.posX, model.supCY, 0]} size={[b.sX, b.SH, b.sZ]} color="#D4A455" />
      ))}
    </group>
  );
}

/* ── Modelo completo de tarima de tacón ── */
function PalletTacon({ dims, units, tablasDet, seps, taconDimDet }) {
  const model = useMemo(() => {
    const det = tablasDet || { superior: [], medio: [], tacon: [], inferior: [] };
    // ── Leer dimensiones en metros con fallbacks ──────────────────
    const SL = toM(dims.superior.largo, units.superior.largo) || 1.22;
    const SA = toM(dims.superior.ancho, units.superior.ancho) || 0.09;
    const SH = toM(dims.superior.alto,  units.superior.alto)  || 0.025;
    const SQ = parseInt(dims.superior.qty) || 5;

    const ML = toM(dims.medio.largo, units.medio.largo) || 1.22;
    const MA = toM(dims.medio.ancho, units.medio.ancho) || 0.09;
    const MH = toM(dims.medio.alto,  units.medio.alto)  || 0.025;
    const MQ = parseInt(dims.medio.qty) || 3;

    const TL = toM(dims.tacon.largo, units.tacon.largo) || 0.09;
    const TA = toM(dims.tacon.ancho, units.tacon.ancho) || 0.09;
    const TH = toM(dims.tacon.alto,  units.tacon.alto)  || 0.12;

    const IL = toM(dims.inferior.largo, units.inferior.largo) || 1.22;
    const IA = toM(dims.inferior.ancho, units.inferior.ancho) || 0.09;
    const IH = toM(dims.inferior.alto,  units.inferior.alto)  || 0.025;
    const IQ = parseInt(dims.inferior.qty) || 5;

    const palletLargo = SL;
    const palletAncho = ML;

    // ── Inferiores ────────────────────────────────────────────────
    // det.inferior[0]      = orilla delantera (corre en X)
    // det.inferior[1..n-2] = interiores perpendiculares (corren en Z)
    // det.inferior[n-1]    = orilla trasera (corre en X)
    const detInf = det.inferior || [];
    const getInfA = (i) => {
      const d = detInf[i];
      return (d && d.ancho !== '') ? (toM(d.ancho, d.unit || units.inferior.ancho) || IA) : IA;
    };
    const infEdgeA0   = getInfA(0);
    const infEdgeA1   = getInfA(IQ - 1);
    const infEdgeZ    = [-palletAncho / 2 + infEdgeA0 / 2, palletAncho / 2 - infEdgeA1 / 2];
    const infMidCount = Math.max(0, IQ - 2);
    const defaultMidLen = palletAncho - infEdgeA0 - infEdgeA1;
    const infMidBoards = Array.from({ length: infMidCount }, (_, i) => {
      const di = detInf[i + 1];
      const sX = (di && di.ancho !== '') ? (toM(di.ancho, di.unit || units.inferior.ancho) || IA) : IA;
      const sZ = (di && di.largo !== '') ? (toM(di.largo, di.unit || units.inferior.largo) || defaultMidLen) : defaultMidLen;
      const posX = infMidCount > 1
        ? -palletLargo / 2 + IA / 2 + i * (palletLargo - IA) / (infMidCount - 1)
        : 0;
      return { posX, sX, sZ };
    });

    // ── Tacones: 9 en cuadrícula 3×3, medidas individuales ───────
    const tacPosZ = [
      -palletAncho / 2 + TA / 2,
      0,
       palletAncho / 2 - TA / 2,
    ];
    const tacPosX = [
      -palletLargo / 2 + TL / 2,
       0,
       palletLargo / 2 - TL / 2,
    ];
    const tacPositions = tacPosX.flatMap(x => tacPosZ.map(z => [x, z]));

    const tacItems = tacPositions.map(([x, z], i) => {
      const d = taconDimDet?.[i];
      const u = d?.unit || units.tacon.largo;
      const tL = (d?.largo && d.largo !== '') ? (toM(d.largo, u) || TL) : TL;
      const tA = (d?.ancho && d.ancho !== '') ? (toM(d.ancho, u) || TA) : TA;
      const tH = (d?.alto  && d.alto  !== '') ? (toM(d.alto,  u) || TH) : TH;
      return { x, z, tL, tA, tH };
    });
    const maxTH = tacItems.length > 0 ? Math.max(...tacItems.map(t => t.tH)) : TH;

    // ── Medias: corren en Z (perpendicular), distribuidas en X ────
    // Ancho individual por tabla (sizeX de cada media)
    const medWidths = Array.from({ length: MQ }, (_, i) => {
      const d = det.medio[i];
      if (d && d.ancho !== '') return toM(d.ancho, d.unit || units.medio.ancho) || MA;
      return MA;
    });
    const medDist = distributeBoards(medWidths, palletLargo);

    // ── Superiores: corren en X (cruzadas a medias), dist. en Z ──
    const supWidths = Array.from({ length: SQ }, (_, i) => {
      const d = det.superior[i];
      if (d && d.ancho !== '') return toM(d.ancho, d.unit || units.superior.ancho) || SA;
      return SA;
    });
    const supSepArr      = seps?.superior || [];
    const defaultSupSepM = toM(dims.superior?.separacion, units.superior?.separacion);
    const hasSups        = defaultSupSepM > 0 || supSepArr.some(s => s && s.sep !== '');
    const supDist        = hasSups
      ? distributeWithSeps(supWidths, supSepArr, defaultSupSepM)
      : distributeBoards(supWidths, palletAncho);

    const yInf   = IH / 2;
    const yMed   = IH + maxTH + MH / 2;
    const yTop   = IH + maxTH + MH + SH / 2;
    const totalH = IH + maxTH + MH + SH;

    return {
      sup:        { boards: supDist.map(b => ({ sizeX: SL, sizeY: SH, sizeZ: b.size, posZ: b.pos })), y: yTop },
      med:        { boards: medDist.map(b => ({ sizeX: b.size, sizeY: MH, sizeZ: ML, posX: b.pos })), y: yMed },
      tacItems,
      IH,
      infEdge:    { sizeX: IL, sizeY: IH, posZ: infEdgeZ, sizeZ0: infEdgeA0, sizeZ1: infEdgeA1, y: yInf },
      infMidBoards,
      totalH,
    };
  }, [dims, units, tablasDet, seps, taconDimDet]);
  return (
    <group position={[0, -model.totalH / 2, 0]}>
      {/* Tablas inferiores — orillas paralelas a superiores (corren en X) */}
      {model.infEdge.posZ.map((z, i) => (
        <Board
          key={`inf-edge-${i}`}
          position={[0, model.infEdge.y, z]}
          size={[model.infEdge.sizeX, model.infEdge.sizeY, i === 0 ? model.infEdge.sizeZ0 : model.infEdge.sizeZ1]}
          color="#B8864A"
        />
      ))}

      {/* Tablas inferiores — interiores perpendiculares (corren en Z) */}
      {model.infMidBoards.map((b, i) => (
        <Board
          key={`inf-mid-${i}`}
          position={[b.posX, model.infEdge.y, 0]}
          size={[b.sX, model.infEdge.sizeY, b.sZ]}
          color="#B8864A"
        />
      ))}

      {/* Tacones — cuadrícula 3×3, medidas individuales */}
      {model.tacItems.map((t, i) => (
        <Board
          key={`tac-${i}`}
          position={[t.x, model.IH + t.tH / 2, t.z]}
          size={[t.tL, t.tH, t.tA]}
          color="#7A5228"
        />
      ))}

      {/* Tablas medias — corren en Z, CRUZADAS a superiores/inferiores */}
      {model.med.boards.map((b, i) => {
        return (
          <Board
            key={`med-${i}`}
            position={[b.posX, model.med.y, 0]}
            size={[b.sizeX, b.sizeY, b.sizeZ]}
            color="#C8973A"
          />
        );
      })}

      {/* Tablas superiores — corren en X, CRUZADAS a medias */}
      {model.sup.boards.map((b, i) => {
        return (
          <Board
            key={`sup-${i}`}
            position={[0, model.sup.y, b.posZ]}
            size={[b.sizeX, b.sizeY, b.sizeZ]}
            color="#D4A455"
          />
        );
      })}

    </group>
  );
}

/* ── Expone el canvas DOM al padre; notifica snapshot al soltar ── */
function SnapshotCapture({ canvasRef, onSnapshot }) {
  const { gl } = useThree();

  useEffect(() => {
    canvasRef.current = gl.domElement;
  }, [gl, canvasRef]);

  const handleEnd = () => {
    if (onSnapshot) onSnapshot(gl.domElement.toDataURL('image/png'));
  };

  return (
    <OrbitControls
      enablePan={false}
      minDistance={0.5}
      maxDistance={4}
      touches={{ ONE: 0, TWO: 2 }}
      enableDamping
      dampingFactor={0.12}
      onEnd={handleEnd}
    />
  );
}

/* ── Cámara que amplía FOV en pantallas angostas — solo al montar ── */
function AdaptiveCamera() {
  const { camera, size } = useThree();
  useEffect(() => {
    const isNarrow = size.width < 500;
    camera.fov = isNarrow ? 62 : 45;
    camera.position.set(
      isNarrow ? 2.2 : 1.8,
      isNarrow ? 1.4 : 1.2,
      isNarrow ? 2.2 : 1.8,
    );
    camera.updateProjectionMatrix();
  }, []); // eslint-disable-line
  return null;
}

/* ── Componente exportable — expone getSnapshot() via ref ── */
const PalletModel3D = forwardRef(function PalletModel3D({ dims, units, tablasDet, tipo, seps, taconDimDet, onSnapshot }, ref) {
  const canvasRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      return canvas.toDataURL('image/png');
    },
  }));

  return (
    <Wrapper>
      <Canvas
        shadows
        camera={{ position: [1.8, 1.2, 1.8], fov: 45 }}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        gl={{ antialias: true, preserveDrawingBuffer: true }}
      >
        <color attach="background" args={['#f5f0ea']} />
        <ambientLight intensity={0.6} />
        <directionalLight position={[3, 5, 3]} intensity={1.2} castShadow shadow-mapSize={[1024, 1024]} />
        <directionalLight position={[-2, 3, -2]} intensity={0.4} />
        <AdaptiveCamera />
        {tipo === 'polin'
          ? <PalletPolin dims={dims} units={units} tablasDet={tablasDet} seps={seps} />
          : <PalletTacon dims={dims} units={units} tablasDet={tablasDet} seps={seps} taconDimDet={taconDimDet} />
        }
        <SnapshotCapture canvasRef={canvasRef} onSnapshot={onSnapshot} />
      </Canvas>
      <Badge>Toca y arrastra para rotar</Badge>
    </Wrapper>
  );
});

export default PalletModel3D;

const Wrapper = styled.div`
  display: block;
  position: relative;
  width: 100%;
  height: 420px;
  background: #f5f0ea;
  border-top: 3px solid var(--blue);
  overflow: hidden;
  touch-action: none;
  @media (max-width: 600px) { height: 300px; }
`;

const Badge = styled.span`
  position: absolute;
  bottom: 0.6rem;
  left: 0.75rem;
  z-index: 10;
  font-size: 0.6rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #888;
  pointer-events: none;
`;
